import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  useAcquireVariantLockMutation,
  useRefreshVariantLockMutation,
} from '../../api/variantApi';
import { selectUser } from '../../authentication/redux/selectors';
import { selectIsUserInstituteAllowed, selectVariantLock } from '../redux';

/**
 * Variant Lock Hook - Manages automatic variant locking
 *
 * Acquires the lock on mount and keeps it alive with a 30s heartbeat while
 * the variant workflow is open. There is deliberately NO "release on close"
 * logic here (no pagehide/beforeunload handling, no cross-tab localStorage
 * bookkeeping) - relying on the backend TTL instead:
 *
 * - Locks expire on the backend after `LOCK_TIMEOUT_MINUTES` without a
 *   heartbeat (see `_get_or_update_variant_lock` in
 *   `selectedvariant_router.py`). Once a tab is closed or the user
 *   navigates away, the heartbeat simply stops and the lock expires on
 *   its own.
 * - The next `acquire` attempt on an expired lock takes it over immediately
 *   (`lock.is_expired()`), so correctness never depends on a cleanup job
 *   having run yet.
 * - `cleanup_expired_variant_locks_task` (Celery beat, every minute) only
 *   does housekeeping on the `VariantLock` table; it is not required for a
 *   lock to become available again.
 *   Relying on the TTL avoids a whole class of frontend release bugs; the
 *   trade-off is that another editor must wait up to `LOCK_TIMEOUT_MINUTES`
 *   after a tab is abandoned before they can take over, instead of
 *   near-instantly.
 *
 * There is no manual "release lock" endpoint anywhere in the app (not even
 * on logout) - the TTL is the single, consistent mechanism for giving up a
 * lock, on the backend as well as the frontend.
 *
 * Mutation hooks from RTK Query replace the old `dispatch(thunk(...)).unwrap()` pattern.
 * The slice state is updated automatically via matchFulfilled/matchRejected matchers in
 * slice.ts (see RTK Query tutorial Part 8: "Updating Toast Display").
 *
 * @param variantId - The ID of the variant to lock
 */
export const useVariantLock = (variantId?: number | string) => {
  const user = useSelector(selectUser);
  const lock = useSelector(selectVariantLock);
  const currentUser = user?.username;

  const isUserInstituteAllowed = useSelector(selectIsUserInstituteAllowed);

  // RTK Query mutation hooks – trigger functions can be called inside effects
  const [acquireVariantLockMutation] = useAcquireVariantLockMutation();
  const [refreshVariantLockMutation] = useRefreshVariantLockMutation();

  const timerRef = useRef<number | null>(null);
  const isAcquiringLock = useRef<boolean>(false);
  const hasAcquiredLock = useRef<boolean>(false);
  const latestLockRef = useRef(lock);

  useEffect(() => {
    latestLockRef.current = lock;
  }, [lock]);

  useEffect(() => {
    if (!variantId || !currentUser || !isUserInstituteAllowed) {
      return;
    }
    if (latestLockRef.current?.lock_type === 'refresh') {
      return;
    }

    // --- 1. LOCK ACQUISITION LOGIC ---
    const performAcquireLock = async () => {
      if (isAcquiringLock.current || hasAcquiredLock.current) {
        return;
      }
      try {
        isAcquiringLock.current = true;
        // Calling the RTK Query mutation trigger – same as tutorial's
        // `await addNewPost({...}).unwrap()` pattern (Part 7).
        const result = await acquireVariantLockMutation({
          variant_id: variantId,
        }).unwrap();

        if (result?.locked_by === currentUser) {
          hasAcquiredLock.current = true;
        }
      } catch (error: any) {
        // error is the transformErrorResponse result: { locked_by, message }
        // (see acquireVariantLock endpoint in variantApi.ts)
        if (error?.locked_by && error?.locked_by !== currentUser) {
          hasAcquiredLock.current = false;
        }
      } finally {
        isAcquiringLock.current = false;
      }
    };

    if (!hasAcquiredLock.current) {
      performAcquireLock();
    }

    // --- 2. HEARTBEAT (30s) ---
    timerRef.current = window.setInterval(async () => {
      if (latestLockRef.current?.lock_type === 'refresh') {
        return;
      }
      const currentLockOwner = latestLockRef.current?.locked_by;

      if (!currentLockOwner || currentLockOwner === currentUser) {
        try {
          await refreshVariantLockMutation({ variant_id: variantId }).unwrap();
        } catch (error: any) {
          const status = error?.status;
          if (status === 404) {
            // SELF-HEALING: lock was lost, try to re-acquire
            hasAcquiredLock.current = false;
            performAcquireLock();
          } else if (status === 403 && timerRef.current) {
            // STOP: another user took the lock
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }
    }, 30 * 1000);

    // --- 3. BACK-FORWARD-CACHE RESTORE ---
    // The heartbeat timer was frozen while the page was bfcache'd, so the
    // lock may meanwhile have expired or been taken over by someone else.
    // Re-validate instead of silently resuming as if we still held it.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      hasAcquiredLock.current = false;
      performAcquireLock();
    };
    window.addEventListener('pageshow', handlePageShow);

    // --- 4. CLEANUP ---
    // No explicit release on unmount/tab-close: the heartbeat simply stops
    // and the lock expires via the backend TTL (see module docblock above).
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      window.removeEventListener('pageshow', handlePageShow);

      hasAcquiredLock.current = false;
      isAcquiringLock.current = false;
    };
  }, [
    variantId,
    currentUser,
    isUserInstituteAllowed,
    acquireVariantLockMutation,
    refreshVariantLockMutation,
    lock?.lock_type,
  ]);
};
